import React, { createContext, useContext, useState, useCallback } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Exchange rates (base currency: PKR)
const EXCHANGE_RATES = {
  PKR: 1.0,
  USD: 0.0036,
  EUR: 0.0031,
  GBP: 0.0026,
  JPY: 0.40,
  CAD: 0.0045,
  AUD: 0.0049,
  CHF: 0.0033,
  CNY: 0.023,
  INR: 0.27,
  BRL: 0.019,
  MXN: 0.072,
  KRW: 4.2,
  SGD: 0.0049,
  HKD: 0.028,
  NZD: 0.0052,
  SEK: 0.031,
  NOK: 0.031,
  DKK: 0.023,
  PLN: 0.014,
  CZK: 0.077,
  HUF: 1.08,
  RUB: 0.26,
  TRY: 0.031,
  ZAR: 0.052,
  ILS: 0.012,
  AED: 0.013,
  SAR: 0.013,
  THB: 0.12,
  MYR: 0.015,
  IDR: 51.0,
  PHP: 0.18,
  VND: 83.0,
};

const CURRENCY_SYMBOLS = {
  PKR: '₨',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RUB: '₽',
  TRY: '₺',
  ZAR: 'R',
  ILS: '₪',
  AED: 'د.إ',
  SAR: '﷼',
  THB: '฿',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  VND: '₫',
};

const CURRENCY_NAMES = {
  PKR: 'Pakistani Rupee',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  JPY: 'Japanese Yen',
  CAD: 'Canadian Dollar',
  AUD: 'Australian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  INR: 'Indian Rupee',
  BRL: 'Brazilian Real',
  MXN: 'Mexican Peso',
  KRW: 'South Korean Won',
  SGD: 'Singapore Dollar',
  HKD: 'Hong Kong Dollar',
  NZD: 'New Zealand Dollar',
  SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',
  DKK: 'Danish Krone',
  PLN: 'Polish Zloty',
  CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint',
  RUB: 'Russian Ruble',
  TRY: 'Turkish Lira',
  ZAR: 'South African Rand',
  ILS: 'Israeli Shekel',
  AED: 'UAE Dirham',
  SAR: 'Saudi Riyal',
  THB: 'Thai Baht',
  MYR: 'Malaysian Ringgit',
  IDR: 'Indonesian Rupiah',
  PHP: 'Philippine Peso',
  VND: 'Vietnamese Dong',
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('PKR');
  const [exchangeRates, setExchangeRates] = useState(EXCHANGE_RATES);

  // Convert price from PKR to selected currency
  const convertPrice = useCallback((pkrPrice, toCurrency = selectedCurrency) => {
    const numeric = typeof pkrPrice === 'number' ? pkrPrice : parseFloat(pkrPrice);
    if (!Number.isFinite(numeric)) return 0;
    const rate = exchangeRates[toCurrency] || 1;
    return numeric * rate;
  }, [selectedCurrency, exchangeRates]);

  // Format price with currency symbol
  const formatPrice = useCallback((pkrPrice, currency = selectedCurrency, showSymbol = true) => {
    const convertedPrice = convertPrice(pkrPrice, currency);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    const decimals = currency === 'JPY' || currency === 'KRW' ? 0 : 2;
    
    if (showSymbol) {
      return `${symbol}${convertedPrice.toFixed(decimals)}`;
    }
    return convertedPrice.toFixed(decimals);
  }, [selectedCurrency, convertPrice]);

  // Get currency symbol
  const getCurrencySymbol = useCallback((currency = selectedCurrency) => {
    return CURRENCY_SYMBOLS[currency] || currency;
  }, [selectedCurrency]);

  // Get currency name
  const getCurrencyName = useCallback((currency = selectedCurrency) => {
    return CURRENCY_NAMES[currency] || currency;
  }, [selectedCurrency]);

  // Get all available currencies
  const getAvailableCurrencies = useCallback(() => {
    return Object.keys(CURRENCY_SYMBOLS).map(code => ({
      code,
      name: CURRENCY_NAMES[code],
      symbol: CURRENCY_SYMBOLS[code],
    }));
  }, []);

  // Update exchange rates (for future API integration)
  const updateExchangeRates = useCallback((newRates) => {
    setExchangeRates(prev => ({ ...prev, ...newRates }));
  }, []);

  // Set selected currency
  const setCurrency = useCallback((currency) => {
    if (CURRENCY_SYMBOLS[currency]) {
      setSelectedCurrency(currency);
    }
  }, []);

  const value = {
    selectedCurrency,
    setCurrency,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
    getCurrencyName,
    getAvailableCurrencies,
    updateExchangeRates,
    exchangeRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
